import Vue from "vue";
import { ObjectModelActionType } from "~/enums/objectModelActionType";
import { ObjectModelDefaultActionType } from "~/enums/objectModelDefaultActionType";
import { Route } from "~/enums/route";
import { RouteHelper } from "~/helpers/routeHelper";
import ObjectModelAction from "~/models/objectModelActions/objectModelAction";
import ObjectModelEntry from "~/models/objectModelEntries/objectModelEntry";
import JobProvider from "~/providers/jobProvider";
import Service from "~/services/service";
import {IObjectModelEntryPerformActionResponse, IServerData} from "~/interfaces/serverInterfaces"
import {Logger} from "~/services/logger"
import processFlowExecutionMixin from '~/mixins/processFlowSubmissions/processFlowExecutionMixin'
import {StringHelper} from '~/helpers/stringHelper'
import ObjectModel from "~/models/objectModel";
import PublishedListView from "~/models/listViews/publishedListView";
import ListViewContentElement from '~/models/listViews/elements/listViewContentElement';
import DetailViewContentElement from '~/models/detailView/elements/detailViewContentElement';
import RefreshEntryDataBus from '../buses/refreshEntryDataBus';
import { IObjectModelActionViewReference } from '~/interfaces/contentManagementInterfaces';

export default Vue.extend({
  mixins: [
    processFlowExecutionMixin
  ],
  props: {
    contentElement: {
      type: Object as () => ListViewContentElement | DetailViewContentElement,
    },
  },
  data: function() {
    return {
      actionErrorTitle: null,
      actionErrorMessage: null,
      currentCustomNotificationActionID: null,
      isPerformingAction: false,
      generatingDocument: false,
      selectedRunProcessAction: null, // used to keep context of performed action post-flow completion
      processFlow: {}, // used when process flow action is running with user interactions
      processFlowSubmissionId: null,
    };
  },
  created() {
    // check required methods
    this.actionReferencesArray;
  },
  computed: {
    actionReferencesArray(): IObjectModelActionViewReference[] {
      console.error("Must define actionReferencesArray computed property in mixed-in component.");
      return;
    },
  },
  methods: {
    performCreateAction(): void {
      window.location.href = this.newEntryPath;
    },
    performEditAction(entry: ObjectModelEntry): void {
      const namedWildcardOptions = RouteHelper.namedWildcardOptionsFactory({
        object_model: entry.object_model_id,
        entry: entry.id
      });
      window.location.href = RouteHelper.path({
        route: Route.objectModelEntriesEdit,
        namedWildcardOptions,
        query: this.objectModelEntryEditFrom
      });
    },
    performDeleteAction(entry: ObjectModelEntry, publishedListView: PublishedListView): void {
      ObjectModelEntry.repo.provider
        .destroy(entry)
        .then(() => {
          ObjectModelEntry.repo.remove(entry);
          if (publishedListView) this.$emit('updateEntries', entry);
          this.$emit('updateConnectedRecords');
          const flash = "Your entry has been successfully deleted from the system.";
          this.redirectToView(flash, {publishedListView: publishedListView});
        })
        .catch(() => {
          this.$store.commit("flashStore/set", {
            flash: {
              alert: "Action cannot be completed at this time."
            }
          });
        });
    },
    performCustomNotificationAction(action: ObjectModelAction): void {
      this.isPerformingAction = false;
      this.currentCustomNotificationActionID = action.id;
      this.showModal(this.vueModalID.objectModelActionCustomNotification);
    },
    performViewDocumentAction(entry: ObjectModelEntry, action: ObjectModelAction): void {
      this.generatingDocument = true;
      this.callPerformActionForEntry(entry, action.id)
        .then(async res => {
          const jobIds = [null, undefined, ""].includes(res[0]["job_ids"]) ? [] : res[0]["job_ids"];
          const jobProvider = new JobProvider(jobIds[0]);
          const pollResponse = await Service.poll({ continue_url: null }, () => jobProvider.fetch());
          this.generatingDocument = false;
          this.isPerformingAction = false;
          window.open(pollResponse.download_path);
        }).catch((res) => {
        this.$store.commit("flashStore/set", {
          flash: {
            alert: `${res.responseJSON.errors}`
          }
        });
      });
    },
    performRunProcessAction(entry: ObjectModelEntry, action: ObjectModelAction) {
      this.selectedRunProcessAction = action;

      const haveEntry = !!entry;
      haveEntry ?
        this.runProcessForEntry(entry, action) :
        this.runProcessForObjectModel(action);
    },
    runProcessForEntry(entry: ObjectModelEntry, action: ObjectModelAction) {
      // detect if job a has been queued for this action
      this.callPerformActionForEntry(entry, action.id)
        .then(this.postFlowCallback)
        .catch(this.logProcessFlowError);
    },
    runProcessForObjectModel(action: ObjectModelAction) {
      const objectModelId = action.parent.object_model_id;
      this.callPerformActionForObjectModel(objectModelId, action.id)
        .then(this.postFlowCallback)
        .catch(this.logProcessFlowError);
    },
    postFlowCallback(response) {
      const data = response[0]
      if (!data.success) {
        this.logProcessFlowError();
        return;
      }
      this.processFlow = data.process_flow
      this.processFlowSubmissionId = data.process_flow_submission_id
      if (data.process_flow.user_interactions_enabled) {
        this.showModal(this.vueModalID.ProcessFlowSubmissionApp);
      } else {
        this.executeProcessFlow({
          onFailure: (result) => {
            this.logProcessFlowError();
          }
        })
      }
    },
    logProcessFlowError() {
      Logger.errorOnce('ObjectModelAction', 'Unable to perform action for process flow.')
    },
    performAction(action: ObjectModelAction, entry: ObjectModelEntry, publishedListView=null): void {
      if (this.isPreview) return;
      this.isPerformingAction = true;

      if (action.is(ObjectModelActionType.DefaultAction)) {
        switch (action.default_action_type) {
          case ObjectModelDefaultActionType.Create:
            this.performCreateAction();
            break;
          case ObjectModelDefaultActionType.Edit:
            this.performEditAction(entry);
            break;
          case ObjectModelDefaultActionType.Delete:
            this.performDeleteAction(entry, publishedListView);
            break;
        }
      } else if (action.action_type === ObjectModelActionType.CustomNotification) {
        this.performCustomNotificationAction(action);
      } else if (action.action_type === ObjectModelActionType.ViewDocument) {
        this.performViewDocumentAction(entry, action);
      } else if (action.action_type === ObjectModelActionType.RunProcess) {
        this.performRunProcessAction(entry, action);
      } else {
        this.defaultPerformAndProcess(entry, action.id);
      }
    },
    defaultPerformAndProcess(entry: ObjectModelEntry, actionId: string, data: object = {}): void {
      if (this.isPreview) return;

      this.callPerformActionForEntry(entry, actionId, data)
        .then(async response => {
          const result = response[0]
          if (result['view_type'] === "detail_view" && !!result['jobs_in_queue']) {
            if (!!result['job_ids']) {
              //subscription to the action cable and showing the modal will happen in the view-detail-view component
              this.$store.commit("objectModelEntriesStore/updateHandleChangesJobIds", result['job_ids']);
            }
          } else {
            this.processActionResult(entry, result)
          }
        })
        .catch(response => {
          this.isPerformingAction = false;
          this.actionErrorTitle = response.statusText;
          this.actionErrorMessage = "You cannot take this action at this time.";
          this.showModal(this.vueModalID.objectModelActionError);
        });
    },
    async callPerformActionForEntry(entry: ObjectModelEntry, actionId: string, data: object = {}): Promise<IServerData<IObjectModelEntryPerformActionResponse>> {
      return ObjectModelEntry.repo.provider.performAction({
        object_model_id: entry.object_model_id,
        entry_id: entry.id,
        action_id: actionId,
        data: {
          ...data,
          perform_context: this.actionPerformContext,
        },
      })
    },
    async callPerformActionForObjectModel(objectModelId: string, actionId: string): Promise<IServerData<IObjectModelEntryPerformActionResponse>> {
      return ObjectModel.repo.provider.performAction({
        object_model_id: objectModelId,
        action_id: actionId,
      })
    },
    processActionResult(entry: ObjectModelEntry, result) {
      this.isPerformingAction = false
      if (!result.success) return;
      switch (result.action) {
        case "none":
          break;
        case "redirect":
          if (result.redirectPath) {
            window.location.href = result.redirectPath;
          }
          break;
        case "redirect_new_tab":
          if (result.redirectPath) {
            window.open(result.redirectPath, "_blank");
          }
          break;
        case "entry_updated":
          entry.loadRootFromJson(result.entry);
          break;
      }
      if (result.message) {
        let flashClass = 'notice';

        if (result.status === 'warning' || result.status === 'finished' && !!result.last_error) {
          flashClass = 'warning';
        } else if (result.status === 'failed') {
          flashClass = 'alert';
        }

        this.$store.commit("flashStore/set", { flash: {[flashClass]: result.message} });
      }
    },
    redirectToView(flash: string, options): void {
      const {publishedListView, modifiedEntryId} = options || {};

      const params = new URLSearchParams(window.location.search);

      // This will evaluate to true if it is a list view action being taken on a detail view
      if (this.selectedPublishedDetailView && this.entry && (this.selectedPublishedDetailView.object_model_id !== this.entry.object_model_id)) {
        window.location.href = RouteHelper.path({
          route: Route.detailView,
          namedWildcardOptions: [
            { wildcard: ":id", modelOrId: this.selectedPublishedDetailView.detail_view_id },
            { wildcard: ":entry_id", modelOrId: this.selectedObjectModelEntry.id }
          ],
          query: { flash }
        });
      } else if (params.get("from_list_view")) {
        const from = params.get("from_one_stop") ? "Verified Professionals" : "Reporting & Submissions Dashboard";
        // List Views are currently housed in Verified Professionals on Onestop. Update when changed.
        window.location.href = RouteHelper.path({
          route: Route.listView,
          modelOrId: params.get("from_list_view"),
          query: { flash, from }
        });
      } else if (this.selectedPublishedDetailView?.published_object_model?.object_model_id) {
        window.location.href = RouteHelper.path({
          route: Route.objectModelsAnalystDashboard,
          modelOrId: this.selectedPublishedDetailView.published_object_model.object_model_id,
          query: { flash }
        });
      } else if (this.selectedPublishedListView || publishedListView) {
        // This is only hit in a delete scenario. It should really be refactored to only call redirectToView from a detail view.
        // Currently this is just a workaround to make it work in both list && detail view.
        this.$store.commit("flashStore/set", {
          flash: {
            notice: "Your entry has been successfully deleted from the system."
          }
        });
      } else {
      }
    },
    handleCloseErrorModal(): void {
      this.hideModal(this.vueModalID.objectModelActionError);
    },
    handleCloseCustomNotificationModal(): void {
      this.hideModal(this.vueModalID.objectModelActionCustomNotification);
    },
    sendCustomNotification(entry: ObjectModelEntry, data: object): void {
      this.defaultPerformAndProcess(entry, this.currentCustomNotificationActionID, data);
      this.currentCustomNotificationActionID = null;
      this.hideModal(this.vueModalID.objectModelActionCustomNotification);
    },
    processFlowExecutionOnFinished(data) {
      this.resetFlow()
      this.processActionResult(null, {...data, success: true})
      this.handleRunProcessDataRefresh();
    },
    processFlowExecutionOnFailed(data) {
      this.resetFlow()
      this.processActionResult(null, {...data, success: true})
      this.handleRunProcessDataRefresh();
    },
    processFlowExecutionOnCancel(): void {
      this.resetFlow()
      this.processActionResult(null, {success: false})
      this.handleRunProcessDataRefresh();
    },
    // Context is already determined through backend through perform_action method
    processFlowExecutionContext() {
      return {}
    },
    resetFlow() {
      this.hideModal(this.vueModalID.ProcessFlowSubmissionApp)
      this.processFlow = null
      this.processFlowSubmissionId = null
    },
    handleRunProcessDataRefresh() {
      if (!this.selectedRunProcessAction) return;

      const actionReference = this.actionReferencesArray.find(ce => ce.action_client_id === this.selectedRunProcessAction.client_id);
      if (actionReference?.refresh_page_on_completion) {
        RefreshEntryDataBus.emitRefresh();
      }

      this.selectedRunProcessAction = null;
    },
  }
});
